/**
 * Visitor tracking — drops a 1-year first-party cookie (mdlgx_vid) on first visit and
 * pings `/api/visits` on every route change. Used by the admin Visitors tab to see
 * the full journey for anonymous visitors and to convert them into leads once they
 * submit any form (lead / contact / quote / careers).
 */
import { api } from "@/lib/api";

const COOKIE = "mdlgx_vid";
const ONE_YEAR_S = 60 * 60 * 24 * 365;

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name, value, maxAge) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers
  return "vid-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

/** Get or create the persistent visitor ID for this browser. */
export function getVisitorId() {
  let v = readCookie(COOKIE);
  if (!v) {
    v = uuid();
    writeCookie(COOKIE, v, ONE_YEAR_S);
  }
  return v;
}

/**
 * Track a single page view. Best-effort, never throws — failures are swallowed so
 * tracking never blocks the UI or surfaces errors to the user.
 */
export async function trackPageView(path) {
  try {
    const visitor_id = getVisitorId();
    const params = new URLSearchParams(window.location.search);
    await api.post("/visits", {
      visitor_id,
      path: path || window.location.pathname,
      referrer: document.referrer || "",
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      screen: `${window.innerWidth}x${window.innerHeight}`,
      lang: navigator.language || "",
    });
  } catch {
    // silent
  }
}
